import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { useParams, Link } from "wouter";
import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PricingCard } from "@/components/pricing/pricing-card";
import { BillingToggle } from "@/components/pricing/billing-toggle";
import { Faqs } from "@/components/pricing/faqs";
import {
  VERTICAL_PRICING,
  type MarketingPlanCode,
  type VerticalPricingSlug,
} from "@/lib/content";

function isVerticalSlug(slug: string | undefined): slug is VerticalPricingSlug {
  return slug === "construction" || slug === "insurance";
}

/**
 * Hits the api-server's public Stripe Checkout endpoint and redirects the
 * buyer to Stripe-hosted checkout. The api-server resolves the actual
 * Stripe price id for the (plan, billing) combo from the synced
 * `stripe.*` schema, so we only ever ship the plan slug + billing cadence
 * over the wire — no price ids in the marketing bundle.
 *
 * `successUrl` lands on the web-app's /register page with a
 * `checkout_session` query param so the user can finish provisioning
 * their workspace. `cancelUrl` brings them back here with `?canceled=1`
 * so we can show a soft toast/banner if we ever want to.
 */
async function startCheckout(opts: {
  plan: MarketingPlanCode;
  billing: "monthly" | "annual";
  vertical: VerticalPricingSlug;
}): Promise<void> {
  // Marketing and the web-app are separate artifacts on the same
  // workspace origin: the marketing site is mounted at `/marketing/`
  // and the web-app at `/app/`. So /app/register on the same origin
  // is the web-app's signup page. We hand Stripe an absolute URL
  // because its checkout substitution happens server-side and needs
  // a fully qualified target.
  const origin = window.location.origin;
  const successUrl = `${origin}/app/register?checkout_session={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${origin}/pricing/${opts.vertical}?canceled=1`;

  const res = await fetch("/api/marketing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan: opts.plan,
      billing: opts.billing,
      successUrl,
      cancelUrl,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
  };
  if (!res.ok || !body.url) {
    throw new Error(body.error || "Could not start checkout");
  }
  window.location.assign(body.url);
}

export default function PricingVerticalPage() {
  const { vertical } = useParams<{ vertical: string }>();
  const [isAnnual, setIsAnnual] = useState(true);
  // Track which CTA is mid-redirect so we can disable buttons + show
  // status. Keyed by plan slug so multiple cards on the page don't all
  // spin when only one was clicked.
  const [pending, setPending] = useState<MarketingPlanCode | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Memoise so referential identity is stable across renders — the
  // PricingCard subtree is otherwise pure and shouldn't re-render
  // every time the parent's `pending` state changes for a sibling.
  const handleCheckout = useCallback(
    (plan: MarketingPlanCode, billing: "monthly" | "annual") => {
      return async () => {
        if (pending) return;
        setCheckoutError(null);
        setPending(plan);
        try {
          await startCheckout({
            plan,
            billing,
            vertical: vertical as VerticalPricingSlug,
          });
          // No setPending(null) on success — the page is being unloaded
          // by the redirect, and clearing state would briefly re-enable
          // the button mid-navigation.
        } catch (err) {
          setPending(null);
          setCheckoutError(
            err instanceof Error ? err.message : "Could not start checkout",
          );
        }
      };
    },
    [pending, vertical],
  );

  if (!isVerticalSlug(vertical)) {
    return (
      <Layout>
        <SEOHead
          title="Vertical pricing not found | ClauseArk"
          description="The vertical pricing page you are looking for does not exist."
        />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-h1 mb-4">Pricing page not found</h1>
          <p className="text-body-lg text-slate-500 mb-8">
            We don&apos;t have vertical pricing for &ldquo;{vertical}&rdquo;.
          </p>
          <Link href="/pricing">
            <Button size="lg">See platform pricing</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const content = VERTICAL_PRICING[vertical];
  const otherSlug: VerticalPricingSlug =
    vertical === "construction" ? "insurance" : "construction";
  const otherLabel = otherSlug === "construction" ? "Construction" : "Insurance";
  // PAYG is always sold as a usage-based subscription on the api-server
  // side (no monthly/annual switch), so we always send "monthly" for it.
  // The price object on Stripe is metered, so the toggle is moot.
  const paygBilling: "monthly" = "monthly";

  return (
    <Layout>
      <SEOHead title={content.seo.title} description={content.seo.description} />

      {/* ── Hero ── */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 text-center">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {content.eyebrow}
            </div>
            <h1
              className="text-display mb-6 text-navy dark:text-white"
              data-testid="vertical-pricing-title"
            >
              {content.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300 mb-10">
              {content.subtitle}
            </p>

            <BillingToggle
              isAnnual={isAnnual}
              onChange={setIsAnnual}
              saveLabel="Save ~17%"
              id={`billing-toggle-${vertical}`}
            />
          </motion.div>
        </div>
      </section>

      {/* Surface a checkout error inline so a buyer who clicks a CTA
          while the api-server hasn't been seeded yet (or while their
          network is offline) sees what happened. */}
      {checkoutError && (
        <div className="container mx-auto px-4 md:px-6 -mt-6 mb-6">
          <div
            className="max-w-2xl mx-auto rounded-xl border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-4 text-sm text-red-800 dark:text-red-200"
            data-testid="checkout-error"
            role="alert"
          >
            {checkoutError}
          </div>
        </div>
      )}

      {/* ── Subscription tiers ── */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {content.plans.map((plan, i) => {
              const price = isAnnual
                ? `$${plan.annualMonthly.toLocaleString()}`
                : `$${plan.monthly.toLocaleString()}`;
              const cadence = "/ mo";
              const billing = isAnnual
                ? `billed annually at $${plan.annualTotal.toLocaleString()}/yr`
                : "billed monthly";
              const isPending = pending === plan.plan;
              return (
                <PricingCard
                  key={plan.plan}
                  index={i}
                  name={plan.name}
                  price={price}
                  cadence={cadence}
                  billing={billing}
                  tagline={plan.tagline}
                  features={[...plan.features]}
                  cta={{
                    label: isPending ? "Redirecting…" : plan.ctaLabel,
                    onClick: handleCheckout(
                      plan.plan,
                      isAnnual ? "annual" : "monthly",
                    ),
                  }}
                  featured={plan.featured}
                  meta={[
                    { label: "Seats", value: plan.seats },
                    { label: "Contracts", value: plan.contracts },
                  ]}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Construction add-ons ROI cross-link ──
          Surfaces the dedicated workflow-pack ROI page only on the
          construction vertical — the page copy is construction-specific
          and the insurance vertical has no equivalent yet. */}
      {vertical === "construction" && (
        <section className="pb-12 -mt-6">
          <div className="container mx-auto px-4 md:px-6">
            <div
              className="max-w-4xl mx-auto rounded-2xl border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/60 dark:bg-indigo-950/30 p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              data-testid="construction-addons-cross-link"
            >
              <div className="md:max-w-xl">
                <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 mb-1">
                  Workflow packs
                </div>
                <p className="text-sm md:text-base text-slate-700 dark:text-slate-200">
                  See the per-project ROI math for the Subcontractor +
                  Compliance, Change Order + Claims, and Lien Waiver + Pay
                  Application packs.
                </p>
              </div>
              <Link href="/pricing/construction/add-ons">
                <Button
                  variant="outline"
                  className="whitespace-nowrap"
                  data-testid="construction-addons-roi-link"
                >
                  ROI for workflow packs
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── Pay-as-you-go ── */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto rounded-3xl border border-border bg-card p-8 md:p-10"
            data-testid="pay-as-you-go-card"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="md:max-w-md">
                <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-2">
                  No subscription
                </div>
                <h3 className="text-h3 mb-2">{content.payAsYouGo.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {content.payAsYouGo.tagline}
                </p>
              </div>
              <div className="md:text-right">
                <div>
                  <span className="text-4xl font-extrabold">
                    {content.payAsYouGo.price}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    {content.payAsYouGo.cadence}
                  </span>
                </div>
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={handleCheckout(content.payAsYouGo.plan, paygBilling)}
                  disabled={pending !== null}
                  data-testid={`payg-cta-${vertical}`}
                >
                  {pending === content.payAsYouGo.plan
                    ? "Redirecting…"
                    : content.payAsYouGo.ctaLabel}
                </Button>
              </div>
            </div>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
              {content.payAsYouGo.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ── Vertical pack highlights ── */}
      <section className="py-24 bg-slate-50 dark:bg-navy/30 border-y border-border/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-4">
                <Sparkles className="h-4 w-4 text-indigo-500" />
                <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                  {content.pack.eyebrow}
                </span>
              </div>
              <h2 className="text-h2 mb-4 text-navy dark:text-white">
                {content.pack.heading}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {content.pack.body}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.pack.highlights.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-2xl border border-border bg-card p-6"
                >
                  <h3 className="text-base font-bold mb-2 text-navy dark:text-white">
                    {h.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {h.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI comparison ── */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-3 tracking-wider uppercase">
                {content.roi.eyebrow}
              </div>
              <h2 className="text-h2 mb-4 text-navy dark:text-white">
                {content.roi.heading}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {content.roi.body}
              </p>
            </div>

            <div
              className="rounded-3xl border border-border bg-card overflow-hidden"
              data-testid="roi-comparison"
            >
              <table className="w-full text-left">
                <tbody className="divide-y divide-border">
                  {content.roi.rows.map((row, i) => (
                    <tr
                      key={i}
                      className={
                        row.emphasis
                          ? "bg-indigo-50/60 dark:bg-indigo-950/30"
                          : ""
                      }
                    >
                      <td className="py-5 px-6 text-sm md:text-base">
                        <span
                          className={
                            row.emphasis
                              ? "font-semibold text-navy dark:text-white"
                              : "text-slate-700 dark:text-slate-300"
                          }
                        >
                          {row.label}
                        </span>
                      </td>
                      <td className="py-5 px-6 text-right">
                        <span
                          className={`text-base md:text-lg ${
                            row.emphasis
                              ? "font-extrabold text-indigo-600 dark:text-indigo-400"
                              : "font-semibold text-foreground"
                          }`}
                        >
                          {row.value}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-xs text-muted-foreground text-center max-w-2xl mx-auto">
              {content.roi.summary}
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <Faqs faqs={content.faqs} />

      {/* ── Final CTA ── */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto rounded-3xl bg-brand-gradient p-10 md:p-14 text-white text-center"
          >
            <h2 className="text-h2 mb-3">{content.cta.heading}</h2>
            <p className="text-body-lg opacity-90 mb-8 max-w-xl mx-auto">
              {content.cta.body}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                className="bg-white text-indigo-700 hover:bg-white/90 h-11 px-6"
                onClick={handleCheckout(
                  content.cta.primary.plan,
                  isAnnual ? "annual" : "monthly",
                )}
                disabled={pending !== null}
                data-testid="final-cta-primary"
              >
                {pending === content.cta.primary.plan
                  ? "Redirecting…"
                  : content.cta.primary.label}
              </Button>
              <Link href={content.cta.secondary.path}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 text-white hover:bg-white/10 h-11 px-6"
                >
                  {content.cta.secondary.label}
                </Button>
              </Link>
            </div>
            <div className="mt-8 text-sm opacity-80">
              Looking for the other vertical?{" "}
              <Link
                href={`/pricing/${otherSlug}`}
                className="underline font-semibold hover:opacity-100"
              >
                See {otherLabel} pricing
                <ArrowRight className="inline h-3.5 w-3.5 ml-0.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
